class Comment {
    private readonly id: number | null;
    private readonly ticketId: string | null;
    private readonly subject: string | null;
    private readonly from: string | null;
    private readonly to: string | null;
    private readonly cc: string | null;
    private readonly body: string | null;
    private readonly date: string | null;
    private readonly articleSenderTypeId: number | null;
    private readonly attachments: string | null;
    private readonly type: string | null;
    private readonly visible: boolean | null;
    private readonly archived: boolean | null;

    constructor(
        id: number | null,
        ticketId: string | null,
        subject: string | null,
        from: string | null,
        to: string | null,
        cc: string | null,
        body: string | null,
        date: string | null,
        articleSenderTypeId: number | null,
        attachments: string | null,
        type: string | null,
        visible: boolean | null,
        archived: boolean | null,
    ) {
        this.id = id;
        this.ticketId = ticketId;
        this.subject = subject;
        this.from = from;
        this.to = to;
        this.cc = cc;
        this.body = body;
        this.date = date;
        this.articleSenderTypeId = articleSenderTypeId;
        this.attachments = attachments;
        this.type = type;
        this.visible = visible;
        this.archived = archived;
    }

    /**
     * Converts a JSON object into a User instance.
     *
     * @param {any} item - The JSON object containing user data to be converted.
     * @return {Comment} A new User instance created from the provided JSON data.
     */
    public static fromJson(item: any): Comment {
        return new Comment(
            item.id,
            item.ticketId,
            item.subject,
            item.from,
            item.to,
            item.cc,
            item.body,
            item.date,
            item.articleSenderTypeId,
            item.attachments,
            item.type,
            item.visible,
            item.archived
        );
    }

    public getSubject(): string | null {
        return this.subject;
    }

    public getTo(): string | null {
        return this.to;
    }

    public hasTo(): boolean {
        return this.to !== null && this.to !== "";
    }

    public getCc(): string | null {
        return this.cc;
    }

    public hasCc(): boolean {
        return this.cc !== null && this.cc !== "";
    }

    public getArticleSenderTypeId(): number | null {
        return this.articleSenderTypeId;
    }

    public getAttachments(): string | null {
        return this.attachments;
    }

    /**
     * Get the archived status of the comment.
     *
     * @return {boolean | null} The archived status.
     */
    public getArchived(): boolean | null {
        return this.archived;
    }

    /**
     * Get the author of the comment.
     *
     * @return {string | null} The author's name.
     */
    public getAuthor(): string | null {
        return this.from;
    }

    /**
     * Get the date associated with the comment.
     *
     * @return {string | null} The date.
     */
    public getDate(): string | null {
        return this.date;
    }

    /**
     * Get the unique identifier of the comment.
     *
     * @return {string | null} The comment ID.
     */
    public getId(): number | null {
        return this.id;
    }

    /**
     * Get the message content of the comment.
     *
     * @return {string | null} The message content.
     */
    public getMessage(): string | null {
        return this.body;
    }

    /**
     * Get the ticket ID associated with the comment.
     *
     * @return {string | null} The ticket ID.
     */
    public getTicketId(): string | null {
        return this.ticketId;
    }

    /**
     * Get the type of the comment.
     *
     * @return {string | null} The comment type.
     */
    public getType(): string | null {
        return this.type;
    }

    /**
     * Get the visibility status of the comment.
     *
     * @return {string | null} The visibility status.
     */
    public getVisible(): boolean | null {
        return this.visible;
    }

    /**
     * Checks if the current object is visible based on its internal state.
     *
     * @return {boolean} True if the object is visible, false otherwise.
     */
    public isVisible(): boolean {
        return this.visible === null
            ? true
            : this.visible;
    }

    /**
     * Checks if the email is from the support team.
     *
     * @return {boolean} Returns true if the email is from 'support@celeste.fr', otherwise false.
     */
    public isFromSupport(): boolean {
        return this.from === null
            ? false
            : this.from.includes('support@celeste.fr');
    }
}

export default Comment;